import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Modal from './Modal';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [modalMessage, setModalMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if we're in development mode
    const isDevelopment = window.location.hostname === 'localhost';
    
    if (isDevelopment) {
      // For local development, just show the form data
      console.log('Form Data (Development Mode):', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message
      });
      
      setModalType('success');
      setModalMessage(`Thank you ${formData.name}! Your message has been received successfully. We'll get back to you soon at ${formData.email}.`);
      setShowModal(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        message: ''
      });
      return;
    }
    
    // For production (Netlify), submit to Netlify Forms
    const form = e.target as HTMLFormElement;
    
    // Create form data with proper encoding for Netlify
    const netlifyFormData = new FormData();
    netlifyFormData.append('form-name', 'contact');
    netlifyFormData.append('name', (form.elements.namedItem('name') as HTMLInputElement).value);
    netlifyFormData.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    netlifyFormData.append('company', (form.elements.namedItem('company') as HTMLInputElement).value);
    netlifyFormData.append('message', (form.elements.namedItem('message') as HTMLTextAreaElement).value);
    
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(netlifyFormData as any).toString()
    })
    .then((response) => {
      if (response.ok) {
        setModalType('success');
        setModalMessage(`Thank you ${formData.name}! Your message has been sent successfully. We'll get back to you soon at ${formData.email}.`);
        setShowModal(true);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    })
    .catch((error) => {
      setModalType('error');
      setModalMessage('There was an error sending your message. Please try again or contact us directly.');
      setShowModal(true);
      console.error('Form submission error:', error);
    });
  };

  return (
    <>
      <section className="section-padding bg-gray-900">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6 lg:space-y-8"
          >
            <div>
              <motion.h2
                className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-white mb-4 lg:mb-6"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Get in{' '}
                <span className="bg-gradient-to-r from-blue-400 via-red-400 to-white bg-clip-text text-transparent">
                  Touch
                </span>
              </motion.h2>
              
              <motion.p
                className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true }}
              >
                Ready to transform your business with innovative technology? Let's discuss 
                how we can help you achieve your goals.
              </motion.p>
            </div>

            {/* Contact Information */}
            <motion.div
              className="space-y-4 lg:space-y-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
            >
              {/* Address */}
              <div className="flex items-start space-x-3 lg:space-x-4">
                <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-400/30">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1 text-sm lg:text-base">Address</h3>
                  <p className="text-gray-300 text-sm lg:text-base leading-relaxed">
                    G/F Livelihood Center Bldg.<br /> 
                    R. Magsaysay Ave. Zone IV Sagapan Poblacion<br />
                    Iba, Zambales, Philippines
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-3 lg:space-x-4">
                <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 bg-red-500/20 rounded-lg flex items-center justify-center border border-red-400/30">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1 text-sm lg:text-base">Email</h3>
                  <p className="text-gray-300 text-sm lg:text-base">support.team@ibaytechcorp.com</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-3 lg:space-x-4">
                <div className="flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center border border-blue-400/30">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1 text-sm lg:text-base">Phone</h3>
                  <p className="text-gray-300 text-sm lg:text-base">+63 917 127 4229</p>
                </div>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              className="pt-4 lg:pt-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-white font-semibold mb-3 lg:mb-4 text-sm lg:text-base">Follow Us</h3>
              <div className="flex space-x-3 lg:space-x-4">
                <a 
                  href="#" 
                  className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center hover:bg-blue-500/30 border border-blue-400/30 transition-colors duration-300"
                >
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500/100" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 lg:w-12 lg:h-12 bg-red-500/20 rounded-lg flex items-center justify-center hover:bg-red-500/30 border border-red-400/30 transition-colors duration-300"
                >
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </a>
                <a 
                  href="#" 
                  className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500/20 rounded-lg flex items-center justify-center hover:bg-blue-500/30 border border-blue-400/30 transition-colors duration-300"
                >
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="glass-card p-4 sm:p-6 lg:p-8 rounded-2xl"
          >
            <form 
              id="contact-form"
              name="contact" 
              method="POST" 
              data-netlify="true" 
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit} 
              className="space-y-4 lg:space-y-6"
            >
              {/* Hidden field for Netlify */}
              <input type="hidden" name="form-name" value="contact" />
              
              {/* Honeypot field for spam protection */}
              <div style={{ display: 'none' }}>
                <label>
                  Don't fill this out if you're human: <input name="bot-field" />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2 text-sm lg:text-base">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2 text-sm lg:text-base">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-white font-medium mb-2 text-sm lg:text-base">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white font-medium mb-2 text-sm lg:text-base">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="form-textarea"
                  placeholder="Tell us about your project or inquiry..."
                />
              </div>

              <motion.button
                type="submit"
                className="w-full py-3 lg:py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:from-red-600 hover:to-red-700 transform hover:scale-105 transition-all duration-300 ease-in-out shadow-lg shadow-red-500/25 text-sm lg:text-base"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Send Message
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Success/Error Modal */}
    <Modal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      title={modalType === 'success' ? 'Message Sent!' : 'Oops! Something went wrong'}
      type={modalType}
    >
      <p className="text-lg leading-relaxed">{modalMessage}</p>
      {modalType === 'success' && (
        <div className="mt-4 text-sm text-gray-400">
          We typically respond within 24 hours during business days.
        </div>
      )}
    </Modal>
  </>
  );
};

export default ContactSection;
